import {
  useCreateExamCourseMutation,
  useSearchExamCourseGroupsLazyQuery,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { CloseRoundedIcon, Dialog, DialogTitle, Divider, IconButton } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import { ExaminationCourseCreationForm } from '../../components';
import type { ExaminationCourseCreationFormInput } from '../../schema';

interface ExaminationCourseCreationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ExaminationCourseCreationModal(
  props: Readonly<ExaminationCourseCreationModalProps>,
) {
  const { open: isOpen, onClose } = props;
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [createExaminationCourse, { loading: isCreatingExaminationCourse }] =
    useCreateExamCourseMutation({
      onCompleted: (data) => {
        onClose();
        const courseId = data?.excCreateExamCourse.examCourse.id;
        showSnackbar({
          message: 'Examination course created successfully',
          variant: 'success',
        });
        navigate('/exam-courses/:courseId/qconfigs', {
          params: {
            courseId: courseId,
          },
        });
      },
    });

  const [
    searchExamCourseGroups,
    { data: examCourseGroupsData, loading: isSearchingExaminationCourseGroups },
  ] = useSearchExamCourseGroupsLazyQuery();

  const handleCreateExaminationCourse = async (data: ExaminationCourseCreationFormInput) => {
    await createExaminationCourse({
      variables: {
        createExamCourseInput: {
          code: data.code,
          name: data.name,
          groupName: data.group?.inputValue ?? data.group?.label ?? undefined,
          type: data.type,
          language: data.language,
        },
      },
    });
  };

  const examCoursesOptions =
    examCourseGroupsData?.excSearchExamCourseGroupsByName.tags.map((tag) => ({
      label: tag.name,
      value: tag.id,
    })) ?? [];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm'>
      <DialogTitle>Create Examination Course</DialogTitle>
      <IconButton
        onClick={onClose}
        disabled={isCreatingExaminationCourse}
        sx={(theme) => ({
          position: 'absolute',
          right: 16,
          top: 16,
          color: theme.palette.text.primary,
        })}
      >
        <CloseRoundedIcon />
      </IconButton>
      <Divider />
      <ExaminationCourseCreationForm
        onSubmit={handleCreateExaminationCourse}
        onClose={onClose}
        slotProps={{
          group: {
            loading: isSearchingExaminationCourseGroups,
            options: examCoursesOptions,
            onSearch: (inputValue) => {
              searchExamCourseGroups({
                fetchPolicy: 'cache-and-network',
                variables: {
                  searchExamCourseGroupsByNameInput: {
                    name: inputValue?.trim() ? inputValue : undefined,
                  },
                },
              });
            },
          },
        }}
      />
    </Dialog>
  );
}
